import moment from "moment-timezone";
import { isNumber } from "mathjs";
import empty from "is-empty";
import { intOrDash, numberOrDash } from "src/utils/number";
import { setDateFormat } from "src/utils";
import CurrencyConverter from "src/utils/CurrencyConversionService";
import styles from "src/styles/agGridCustom.module.scss";
import {
    CONFIRM_EDIT_MODE,
    DEFAULT_CCY_OPTIONS,
    formatStatus,
    HUMANIZED_SETTLEMENT_STATUSES,
    INTERMEDIARY_LABELS,
    TX_TYPE_LABELS,
} from "src/redux/Confirmations/constants";
import DatePickerEditor from "src/components/common/AgGrid/DatePicker";
import { ConfirmationSelectors } from "src/redux/Confirmations/selectors";
import { store } from "src/index";
import { TRU_MAX_AMOUNT, TX_PURPOSE_SELECT_LABELS } from "src/utils/validators";
import * as Validators from "src/utils/validators";
import { getTermFromDate } from "src/utils";
import getHeaderValue from "src/components/common/AgGrid/headerGetter";
import {
    HUMANIZED_OBJECTIVES,
    HUMANIZED_TYPES,
    OBJECTIVES,
} from "src/redux/Tx/constants";
import DropdownItemRenderer from "src/components/common/AgGrid/DropdownItemRenderer";
import { isEmpty, isFunction } from "lodash";
import { ConfirmationActions } from "src/redux/Confirmations/actions";
import CustomRangeFilter from "src/components/common/AgGrid/CustomRangeFilter";
import CustomDateRangeFilter from "src/components/common/AgGrid/CustomDateRangeFilter";

export const CommonColumnDefs = {
    resizable: true,
    lockPinned: true,
    sortable: true,
    enableValue: true,
    enableRowGroup: true,
    enablePivot: true,
    tooltipComponent: "confirmTooltip",
    filter: true,
};

const formatterByField = (
    fieldName,
    value,
    isRowGroup = false,
    isPortfolioTrade = false
) => {
    // TODO: figure out why this grouping special case was added here
    if (isRowGroup && !isPortfolioTrade) return "";

    if (fieldName === "value_date" || fieldName === "min_return_date") {
        return setDateFormat(value, "/") || "-";
    } else if (fieldName === "maturity_date") {
        return setDateFormat(value, "/") || "9999/12/31";
    } else if (fieldName === "term") {
        return value ? `${value} days` : "-";
    } else if (fieldName === "rate" || fieldName === "dividend") {
        return numberOrDash({ value });
    } else if (fieldName === "value") {
        const floatVal = parseFloat(value);
        if (!isNumber(floatVal) || floatVal < 0 || floatVal > TRU_MAX_AMOUNT)
            return "ERROR";
        return intOrDash({ value, replacement: "ERROR" });
    } else if (fieldName === "quantity" || fieldName === "value") {
        const res = intOrDash({ value });

        if (isPortfolioTrade && isRowGroup) {
            return `${res} CU`;
        } else {
            return res;
        }
    } else if (fieldName === "callable") {
        return empty(value) ? "-" : value ? "Y" : "N";
    } else if (
        fieldName === "confirmed_time" ||
        fieldName === "last_updated_time"
    ) {
        return moment
            .utc(value)
            .tz(moment.tz.guess())
            .format("YYYY/MM/DD HH:mm:ss");
    } else if (fieldName === "type") {
        return HUMANIZED_TYPES[value];
    } else if (fieldName === "objective") {
        return empty(value) ? "-" : HUMANIZED_OBJECTIVES[value] || value; // should handle a free text value which is legal on Api
    } else if (fieldName === "status") {
        return formatStatus(value);
    } else if (fieldName === "benchmark_fee" || fieldName === "my_perf_bps") {
        return numberOrDash({ value });
    } else if (fieldName === "my_perf_pa") {
        return intOrDash({ value: Math.round(value), replacement: "-" });
    } else {
        return value || "-";
    }
};

// NOTE: For Server Side Data Model, node object does not have childIndex
const getRowNum = (params) =>
    params.node.group && !params.data.is_portfolio_trade
        ? ""
        : params.node.level
        ? params.node.rowIndex - params.node.parent.rowIndex
        : params.node.rowIndex + 1;

const originValue = (params) => {
    const editMode = ConfirmationSelectors.GetEditMode(store.getState());
    if (editMode !== CONFIRM_EDIT_MODE.ON) return null;
    const changes = ConfirmationSelectors.GetTableChanges(store.getState());
    if (empty(changes[params.data.confirmation_id])) return null;
    const fieldName = params.colDef.field;
    if (
        (fieldName === "term" &&
            !(
                "maturity_date" in
                changes[params.data.confirmation_id].originValue
            )) ||
        (fieldName !== "term" &&
            !(fieldName in changes[params.data.confirmation_id].originValue))
    )
        return null;

    let originValue =
        changes[params.data.confirmation_id].originValue[fieldName];
    if (fieldName === "term") {
        originValue =
            changes[params.data.confirmation_id].originValue["maturity_date"];
    }

    return formatterByField(fieldName, originValue);
};

const errorType = (params) => {
    const editMode = ConfirmationSelectors.GetEditMode(store.getState());
    if (editMode !== CONFIRM_EDIT_MODE.ON) return null;
    const errors = ConfirmationSelectors.GetTableErrors(store.getState());
    if (
        empty(errors) ||
        empty(errors[params.data.confirmation_id]) ||
        empty(errors[params.data.confirmation_id][params.colDef.field])
    )
        return null;
    return errors[params.data.confirmation_id][params.colDef.field];
};

const cancelError = (params) => {
    const editMode = ConfirmationSelectors.GetEditMode(store.getState());
    if (editMode === CONFIRM_EDIT_MODE.ON) return false;
    const errors = ConfirmationSelectors.GetCancelErrors(store.getState());
    return errors && errors.includes(params.data?.confirmation_id);
};

export const RowClassRules = {
    [styles.agRowError]: (params) => cancelError(params),
};
const filterOptions = ["inRange", "greaterThan", "lessThan"];
const isRowEditable = (params) => {
    const editMode = ConfirmationSelectors.GetEditMode(store.getState());
    return (
        editMode === CONFIRM_EDIT_MODE.ON &&
        params.data.type !== "HOLD" &&
        params.data.status === "CONFIRMED" && //TODO: check this
        params.data.source !== "External" &&
        !params.data.IsPartnerTrade &&
        Validators.isCancellable(
            params.data.value_date,
            params.data.security_country
        )
    );
};

const isColumnEditable = (params) => {
    if (isFunction(params.colDef.editable))
        return params.colDef.editable(params);
    return params.colDef.editable;
};

const getConfirmationsFilterOptions = async (key, params, callback) => {
    try {
        store.dispatch(ConfirmationActions.fetchFilterOptions(key)).then(() => {
            const state = store.getState();
            const values =
                ConfirmationSelectors.GetConfirmationsFilterOptions(state);
            callback(values?.[key]);
        });
    } catch (error) {
        console.error("Error fetching filter options:", error);
    }
};

const getNewFilterOptions = (key, params) => {
    getConfirmationsFilterOptions(key, params, (values) => {
        let filterOptions;
        if (key === "status") {
            filterOptions = values.map(formatStatus);
        } else {
            filterOptions = values;
        }
        params.success(filterOptions);
    });
};
// columns
export const getConfirmationDetailColumnDefs = (
    isLender,
    canEditAll = false,
    enabledPurpose,
    enabledEtfTrades
) => {
    let columns = [
        enabledEtfTrades
            ? {
                  headerName: "Constituents",
                  field: "isPortfolioTrade",
                  headerClass: "header-constituents",
                  colId: "used_constituents",
                  width: 100,
                  minWidth: 100,
                  valueGetter: (params) => {
                      if (
                          params.data &&
                          params.data.used_constituents &&
                          params.data.total_constituents &&
                          !params.data.parent_id &&
                          params.node
                      ) {
                          const activeCount = params.data.used_constituents;
                          const totalCount = params.data.total_constituents;
                          return `${activeCount} / ${totalCount}`;
                      }

                      return "";
                  },
              }
            : {},
        {
            headerName: "Num",
            colId: "row_num",
            valueGetter: (params) => getRowNum(params),
            width: 60,
            cellClass: [styles.agCenterAlign],
            sortable: false,
            resizable: false,
            enableValue: false,
            enableRowGroup: false,
            enablePivot: false,
            suppressMenu: true,
        },
        isLender
            ? {
                  headerName: "Borrower",
                  field: "borrower_name",
                  headerClass: "header-borrower",
                  minWidth: 230,
                  flex: 23,
                  filterParams: {
                      values: (params) =>
                          getNewFilterOptions("borrower_name", params),
                      buttons: ["clear"],
                  },
                  cellClassRules: {
                      [styles.agConfirmCellError]: (params) =>
                          errorType(params) !== null ||
                          (cancelError(params) && params.data.IsPartnerTrade),
                  },
              }
            : {
                  headerName: "Lender",
                  field: "lender_name",
                  headerClass: "header-lender",
                  minWidth: 230,
                  flex: 23,
                  filterParams: {
                      values: (params) =>
                          getNewFilterOptions("lender_name", params),
                      buttons: ["clear"],
                  },
              },
        {
            headerName: "ID",
            field: "confirmation_id",
            headerClass: "header-confirmation-id",
            minWidth: 160,
            flex: 16,
            filterParams: {
                values: (params) =>
                    getNewFilterOptions("confirmation_id", params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Ticker",
            field: "security_ticker",
            headerClass: "header-security-ticker",
            minWidth: 100,
            flex: 10,
            cellClass: ["data-type-string"],
            filterParams: {
                values: (params) =>
                    getNewFilterOptions("security_ticker", params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "ISIN",
            field: "security_isin",
            headerClass: "header-security-isin",
            minWidth: 110,
            flex: 10,
            cellClass: ["data-type-string"],
            filterParams: {
                values: (params) =>
                    getNewFilterOptions("security_isin", params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "BBG Ticker",
            field: "security_bbg_code",
            headerClass: "header-security-bbg-code",
            minWidth: 100,
            flex: 10,
            cellClass: ["data-type-string"],
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Security Name",
            field: "security_name",
            headerClass: "header-security-name",
            minWidth: 200,
            flex: 20,
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Security Name (Local)",
            field: "security_local_name",
            headerClass: "header-security-local-name",
            minWidth: 200,
            flex: 20,
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Qty",
            field: "quantity",
            headerClass: "header-quantity",
            minWidth: 90,
            flex: 9,
            editable: canEditAll ? isRowEditable : false,
            cellClass: styles.agRightAlign,
            cellClassRules: {
                [styles.agConfirmModified]: (params) =>
                    originValue(params) !== null,
                [styles.agConfirmCellError]: (params) =>
                    errorType(params) !== null,
                [styles.agConfirmEditable]: isColumnEditable,
            },
            tooltipField: "quantity",
            tooltipComponentParams: { type: "confirmation" },
            valueFormatter: (params) => {
                if (errorType(params)) return params.value;
                return formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                );
            },
            filter: CustomRangeFilter,
            filterParams: {
                buttons: ["clear"],
                suppressAndOrCondition: true,
                filterOptions: ["inRange"],
            },
        },
        {
            // TODO: this is temporary. We should switch between
            // "fee_rateBp" (Fee) and "rateRebate_rateBp" (Rebate)
            headerName: "Rate (%)",
            field: "rate",
            headerClass: "header-rate",
            minWidth: 90,
            flex: 9,
            editable: canEditAll ? isRowEditable : false,
            cellClass: styles.agRightAlign,
            cellClassRules: {
                [styles.agConfirmModified]: (params) =>
                    originValue(params) !== null,
                [styles.agConfirmCellError]: (params) =>
                    errorType(params) !== null,
                [styles.agConfirmEditable]: isColumnEditable,
            },
            tooltipField: "rate",
            tooltipComponentParams: { type: "confirmation" },
            valueFormatter: (params) => {
                if (errorType(params)) return params.value;
                return formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                );
            },
            filter: CustomRangeFilter,
            filterParams: {
                buttons: ["clear"],
                suppressAndOrCondition: true,
                filterOptions: filterOptions,
            },
        },
        {
            headerName: "V/D",
            field: "value_date",
            headerClass: "header-value-date",
            minWidth: 90,
            flex: 9,
            editable: canEditAll ? isRowEditable : false,
            cellEditor: DatePickerEditor,
            cellEditorPopup: true,
            cellClass: styles.agRightAlign,
            cellClassRules: {
                [styles.agConfirmModified]: (params) =>
                    originValue(params) !== null,
                [styles.agConfirmCellError]: (params) =>
                    errorType(params) !== null ||
                    (cancelError(params) &&
                        !Validators.isCancellable(
                            params.value,
                            params.data.security_country
                        )),
                [styles.agConfirmEditable]: isColumnEditable,
            },
            tooltipField: "value_date",
            tooltipComponentParams: { type: "confirmation" },
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filter: CustomDateRangeFilter,
            filterParams: {
                suppressAndOrCondition: true,
                buttons: ["clear"],
                filterOptions: filterOptions,
            },
        },
        {
            headerName: "Min Return Date",
            field: "min_return_date",
            headerClass: "header-min-return-date",
            minWidth: 120,
            flex: 9,
            cellClass: [styles.agCenterAlign],
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filter: CustomDateRangeFilter,
            filterParams: {
                filterOptions: filterOptions,
                buttons: ["clear"],
                suppressAndOrCondition: true,
            },
        },
        {
            headerName: "Same Day Returnable",
            field: "same_day_returnable",
            headerClass: "header-same-day-returnable",
            minWidth: 120,
            flex: 9,
            cellClass: [styles.agCenterAlign],
            valueFormatter: (params) => params.value || "Y",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Records Type",
            field: "type",
            headerClass: "header-type",
            minWidth: 100,
            flex: 7,
            cellClass: [styles.agCenterAlign],
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filterParams: {
                values: ["Bid", "EA", "Regular"],
                buttons: ["clear"],
            },
        },
        {
            headerName: "Status",
            field: "status",
            headerClass: "header-status",
            minWidth: 90,
            flex: 9,
            cellClassRules: {
                [styles.agConfirmCellError]: (params) =>
                    cancelError(params) &&
                    (params.value === "CANCELED" || params.value === "EXPIRED"),
            },
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Objective",
            field: "objective",
            headerClass: "header-objective",
            minWidth: 100,
            flex: 7,
            editable: isRowEditable,
            cellClass: [styles.agLeftAlign],
            cellClassRules: {
                [styles.agConfirmModified]: (params) =>
                    originValue(params) !== null,
                [styles.agConfirmEditable]: isColumnEditable,
            },
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: {
                values: [
                    "-",
                    OBJECTIVES.REGULAR,
                    OBJECTIVES.LP,
                    OBJECTIVES.FINANCING,
                ],
                formatValue: (value) => HUMANIZED_OBJECTIVES[value] || "-",
                cellRenderer: DropdownItemRenderer,
            },
            tooltipField: "objective",
            tooltipComponentParams: { type: "confirmation" },
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            // to match empty value to "-" option in the dropdown selector
            valueGetter: (params) =>
                empty(params.data.objective) ? "-" : params.data.objective,
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Recorded Time",
            field: "confirmed_time",
            headerClass: "header-confirmed-time",
            minWidth: 150,
            flex: 15,
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filter: CustomDateRangeFilter,
            filterParams: {
                buttons: ["clear"],
                suppressAndOrCondition: true,
                filterOptions: filterOptions,
            },
        },
        {
            headerName: "Last Updated",
            field: "last_updated_time",
            headerClass: "header-last-updated-time",
            minWidth: 150,
            flex: 15,
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filter: CustomDateRangeFilter,
            filterParams: {
                buttons: ["clear"],
                suppressAndOrCondition: true,
                filterOptions: filterOptions,
            },
        },
        {
            headerName: "Comment",
            field: "comment",
            headerClass: "header-comment",
            minWidth: 120,
            flex: 12,
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Maturity",
            field: "maturity_date",
            headerClass: "header-maturity-date",
            minWidth: 120,
            flex: 9,
            editable: canEditAll ? isRowEditable : false,
            cellEditor: DatePickerEditor,
            cellEditorPopup: true,
            cellClass: styles.agCenterAlign,
            tooltipField: "maturity_date",
            tooltipComponentParams: { type: "confirmation" },
            cellClassRules: {
                [styles.agConfirmModified]: (params) =>
                    originValue(params) !== null,
                [styles.agConfirmCellError]: (params) =>
                    errorType(params) !== null,
                [styles.agConfirmEditable]: isColumnEditable,
            },
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filter: CustomDateRangeFilter,
            filterParams: {
                buttons: ["clear"],
                suppressAndOrCondition: true,
                filterOptions: filterOptions,
            },
        },
        {
            headerName: "Days to Maturity",
            field: "term",
            headerClass: "header-term",
            minWidth: 90,
            flex: 7,
            tooltipField: "term",
            tooltipComponentParams: { type: "confirmation" },
            cellClass: [styles.agCenterAlign],
            valueGetter: (params) =>
                getTermFromDate(
                    params.data.value_date,
                    params.data.maturity_date
                ),
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filter: CustomRangeFilter,
            filterParams: {
                filterOptions: filterOptions,
                buttons: ["clear"],
                suppressAndOrCondition: true,
            },
        },
        !isLender && enabledPurpose
            ? {
                  headerName: "Purpose",
                  field: "purpose",
                  colId: "Details Purpose",
                  minWidth: 100,
                  flex: 10,
                  valueFormatter: (params) => {
                      return TX_PURPOSE_SELECT_LABELS(params.value);
                  },
              }
            : {},
        {
            headerName: "Callable",
            field: "callable",
            headerClass: "header-callable",
            minWidth: 80,
            flex: 8,
            editable: canEditAll ? isRowEditable : false,
            cellClass: styles.agRightAlign,
            cellClassRules: {
                [styles.agConfirmModified]: (params) =>
                    originValue(params) !== null,
                [styles.agConfirmCellError]: (params) =>
                    errorType(params) !== null,
                [styles.agConfirmEditable]: isColumnEditable,
            },
            tooltipField: "callable",
            tooltipComponentParams: { type: "confirmation" },
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: {
                values: [true, false],
                formatValue: (value) => (value ? "Y" : "N"),
                cellRenderer: DropdownItemRenderer,
            },
            filter: false,
            // backend returns an error so turning this filter off
        },
        {
            headerName: "Div(%)",
            field: "dividend",
            headerClass: "header-dividend",
            minWidth: 90,
            flex: 9,
            editable: canEditAll ? isRowEditable : false,
            cellClass: styles.agRightAlign,
            cellClassRules: {
                [styles.agConfirmModified]: (params) =>
                    originValue(params) !== null,
                [styles.agConfirmCellError]: (params) =>
                    errorType(params) !== null,
                [styles.agConfirmEditable]: isColumnEditable,
            },
            tooltipField: "dividend",
            tooltipComponentParams: { type: "confirmation" },
            valueFormatter: (params) => {
                if (errorType(params)) return params.value;
                return formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                );
            },
            filter: CustomRangeFilter,
            filterParams: {
                filterOptions: filterOptions,
                buttons: ["clear"],
                suppressAndOrCondition: true,
            },
        },
        {
            headerName: "Coll\nType",
            field: "collateral_type",
            minWidth: 90,
            flex: 7,
            headerClass: [
                "header-collateral-type",
                styles.agHeaderPre,
                styles.agCenterAlign,
            ],
            editable: canEditAll ? isRowEditable : false,
            cellClass: styles.agRightAlign,
            cellClassRules: {
                [styles.agConfirmModified]: (params) =>
                    originValue(params) !== null,
                [styles.agConfirmCellError]: (params) =>
                    errorType(params) !== null,
                [styles.agConfirmEditable]: isColumnEditable,
            },
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: {
                values: ["CA", "NC", "CP"],
                formatValue: (value) => (empty(value) ? "-" : value),
                cellRenderer: DropdownItemRenderer,
            },
            tooltipField: "collateral_type",
            tooltipComponentParams: { type: "confirmation" },
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Coll\nCCY",
            field: "collateral_currency",
            headerClass: [
                "header-collateral-currency",
                styles.agHeaderPre,
                styles.agCenterAlign,
            ],
            minWidth: 90,
            flex: 9,
            editable: canEditAll ? isRowEditable : false,
            cellClass: styles.agRightAlign,
            cellClassRules: {
                [styles.agConfirmModified]: (params) =>
                    originValue(params) !== null,
                [styles.agConfirmCellError]: (params) =>
                    errorType(params) !== null,
                [styles.agConfirmEditable]: isColumnEditable,
            },
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: (params) => ({
                values: params.data.SelectableCCY || DEFAULT_CCY_OPTIONS,
                formatValue: (value) => (empty(value) ? "-" : value),
                cellRenderer: DropdownItemRenderer,
            }),
            tooltipField: "collateral_currency",
            tooltipComponentParams: { type: "confirmation" },
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "F/R",
            field: "fr",
            headerClass: "header-fr",
            minWidth: 70,
            flex: 7,
            cellClass: [styles.agCenterAlign],
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerValueGetter: (params) => getHeaderValue(params),
            headerName: "Value",
            field: "value",
            headerClass: "header-value",
            minWidth: 120,
            flex: 12,
            cellClassRules: {
                [styles.agConfirmCellError]: (params) =>
                    params.colDef.valueFormatter(params) === "ERROR",
            },
            cellClass: [styles.agRightAlign],
            valueGetter: (params) =>
                CurrencyConverter.convertFromCountry(
                    params.data.security_closing_price * params.data.quantity,
                    params.data.security_country
                ).value(),
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filter: CustomRangeFilter,
            filterParams: {
                filterOptions: filterOptions,
                buttons: ["clear"],
                suppressAndOrCondition: true,
            },
        },
        {
            headerName: "Benchmark Fee",
            field: "benchmark_fee",
            minWidth: 120,
            flex: 12,
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filter: CustomRangeFilter,
            filterParams: {
                filterOptions: filterOptions,
                buttons: ["clear"],
                suppressAndOrCondition: true,
            },
        },
        {
            headerName: "My Perf. (bps)",
            field: "my_perf_bps",
            minWidth: 120,
            flex: 12,
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filter: CustomRangeFilter,
            filterParams: {
                filterOptions: filterOptions,
                buttons: ["clear"],
                suppressAndOrCondition: true,
            },
        },
        {
            headerName: "My Perf. (pa)",
            field: "my_perf_pa",
            minWidth: 120,
            flex: 12,
            valueFormatter: (params) =>
                formatterByField(
                    params.colDef.field,
                    params.value,
                    params.node.group,
                    params.data.is_portfolio_trade
                ),
            filter: CustomRangeFilter,
            filterParams: {
                filterOptions: filterOptions,
                buttons: ["clear"],
                suppressAndOrCondition: true,
            },
        },
        {
            headerName: "Attachments",
            field: "attachments",
            cellRenderer: "attachments",
            minWidth: 100,
            flex: 10,
            filter: false,
        },
        {
            headerName: "Source",
            field: "source",
            minWidth: 90,
            flex: 8,
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Intermediary\n(중개기관)",
            field: "intermediary",
            minWidth: 120,
            flex: 12,
            valueFormatter: (params) =>
                INTERMEDIARY_LABELS[params.value] || INTERMEDIARY_LABELS.Any,
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Type\n(구분)",
            field: "tx_type",
            minWidth: 120,
            flex: 12,
            valueFormatter: (params) =>
                TX_TYPE_LABELS[params.value] || TX_TYPE_LABELS.Any,
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Internal Fund Code\n(내부 펀드 코드)",
            field: "internal_fund_code",
            minWidth: 180,
            flex: 18,
            valueFormatter: (params) => params.value || "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "My Account Code\n(내 계좌 코드)",
            field: "my_account_code",
            minWidth: 180,
            flex: 18,
            valueFormatter: (params) => params.value || "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "My SLB Number\n(내 대차계정 번호)",
            field: "my_slb_number",
            minWidth: 180,
            flex: 18,
            valueFormatter: (params) => params.value || "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "My Fund Code\n(내 펀드 코드)",
            field: "my_fund_code",
            minWidth: 180,
            flex: 18,
            valueFormatter: (params) => params.value || "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        isLender
            ? {
                  headerName: "List Name\n",
                  field: "list_name",
                  minWidth: 180,
                  flex: 18,
                  valueFormatter: (params) => params.value || "-",
                  filterParams: {
                      values: (params) =>
                          getNewFilterOptions(params.colDef.field, params),
                      buttons: ["clear"],
                  },
              }
            : {},
        {
            headerName: "My Custody Asset Type\n(내 예탁재산구분 코드)",
            field: "my_custody_asset_type",
            minWidth: 180,
            flex: 18,
            valueFormatter: (params) => params.value || "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        !isLender
            ? {
                  headerName: "CPTY User Name",
                  field: "cpty_trader_name",
                  minWidth: 200,
                  flex: 20,
                  valueFormatter: (params) => params.value || "-",
                  filterParams: {
                      values: (params) =>
                          getNewFilterOptions(params.colDef.field, params),
                      buttons: ["clear"],
                  },
              }
            : {},
        {
            headerName: "CPTY Account Code\n(상대방 계좌 코드)",
            field: "cpty_account_code",
            minWidth: 180,
            flex: 18,
            valueFormatter: (params) => params.value || "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "CPTY SLB Number\n(상대방 대차계정 번호)",
            field: "cpty_slb_number",
            minWidth: 180,
            flex: 18,
            valueFormatter: (params) => params.value || "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "CPTY Fund Code\n(상대방 펀드 코드)",
            field: "cpty_fund_code",
            minWidth: 180,
            flex: 18,
            valueFormatter: (params) => params.value || "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "CPTY Custody Asset Type\n(상대방 예탁재산구분 코드)",
            field: "cpty_custody_asset_type",
            minWidth: 200,
            flex: 20,
            valueFormatter: (params) => params.value || "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Settlement Status",
            field: "settlement_status",
            headerClass: "header-settlement_status",
            minWidth: 120,
            flex: 9,
            valueFormatter: (params) =>
                params.value
                    ? HUMANIZED_SETTLEMENT_STATUSES[params.value] || "-"
                    : "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Intermediary ID",
            field: "intermediary_id",
            headerClass: "header-intermediary-id",
            minWidth: 120,
            flex: 12,
            cellClass: [styles.agCenterAlign],
            valueFormatter: (params) => params.value || "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Country",
            field: "security_country",
            headerClass: "header-security-country",
            minWidth: 100,
            flex: 9,
            cellClass: [styles.agCenterAlign],
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
        {
            headerName: "Ref ID",
            field: "order_id",
            headerClass: "header-order-id",
            minWidth: 90,
            flex: 8,
            hide: true,
            cellClass: [styles.agCenterAlign],
            valueFormatter: (params) => params.value || "-",
            filterParams: {
                values: (params) =>
                    getNewFilterOptions(params.colDef.field, params),
                buttons: ["clear"],
            },
        },
    ];
    return columns.filter((col) => !isEmpty(col));
};

export {
    originValue as confirmationOriginValue,
    formatterByField as confirmationFormatterByField,
    errorType as confirmationErrorType,
    isRowEditable as isConfirmationEditable,
};
